import type { OptionData } from 'venn-api';
import { assert, assertNotNil, logExceptionIntoSentry } from 'venn-utils';
import moment from 'moment';

type ParsedOption = Pick<OptionData, 'optionType' | 'strike' | 'expiration'> & { underlyingSymbol: string };

/**
 * Parses an option symbol into its components.
 * Example: "AAPL 230616C00150000" -> { optionType: 'CALL', strike: 150, expiration: '2023-06-16', symbol: 'AAPL' }
 * @param optionSymbol
 */
export const parseOptionSymbol = (optionSymbol?: string): ParsedOption | null => {
  if (!optionSymbol) {
    return null;
  }

  try {
    let [underlyingSymbol, rest] = optionSymbol.toUpperCase().replace(/\s+/, ' ').split(' ', 2);

    underlyingSymbol = assertNotNil(underlyingSymbol);
    rest = assertNotNil(rest);

    const expirationString = rest.slice(0, 6);
    const optionTypeChar = rest.slice(6, 7);
    const strikeString = rest.slice(7);

    const expiration = moment.utc(expirationString, 'YYMMDD');

    assert(expiration.isValid());
    assert(optionTypeChar === 'C' || optionTypeChar === 'P');
    assert(/\d+/.exec(strikeString));

    const strike = parseInt(strikeString, 10) / 1000;
    const optionType = optionTypeChar === 'C' ? 'CALL' : 'PUT';

    return {
      underlyingSymbol,
      expiration: expiration.endOf('day').valueOf(),
      strike,
      optionType,
    };
  } catch {
    logExceptionIntoSentry(`Failed to parse option symbol: "${optionSymbol}"`);
    return null;
  }
};
